'use client'

import { cn } from 'app/lib/utils'
import { getUserByIDAction, searchUsersAction } from 'components/AdminComponents/Users/actionsUser'
import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar'
import { Button } from 'components/ui/button'
import { useCallback, useEffect, useState } from 'react'
import { IoIosSearch, IoMdAdd, IoMdClose, IoMdTrash } from 'react-icons/io'
import { Input } from 'ui/input'
import { Label } from 'ui/label'

interface UserSearchSelectProps {
    name: string
    label: string
    placeholder?: string
    value?: string | IUser | IUserMini
    onChange: (value: string) => void
    disabled?: boolean
    className?: string
}

const UserSearchSelect = ({
    name,
    label,
    placeholder = 'Search for users...',
    value,
    onChange,
    disabled = false,
    className
}: UserSearchSelectProps) => {
    const [searchQuery, setSearchQuery] = useState('')
    const [searchResults, setSearchResults] = useState<IUser[]>([])
    const [selectedUserData, setSelectedUserData] = useState<IUser | IUserMini | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [showResults, setShowResults] = useState(false)
    const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null)

    const getUserById = async (userId: string): Promise<IUserMini | null> => {
        const emptyUser: IUserMini = {
            id: userId,
            username: userId,
            first_name: '',
            last_name: '',
            logo: '',
            premium: false
        }
        try {
            const res = await getUserByIDAction(userId)
            const user = res?.data
            if (res?.success && user) {
                const u = user as IUser
                return {
                    id: u.id,
                    username: u.username ?? '',
                    first_name: u.first_name ?? '',
                    last_name: u.last_name ?? '',
                    logo: u.logo ?? '',
                    premium: u.premium ?? false
                }
            }
            return emptyUser
        } catch (error) {
            return emptyUser
        }
    }

    useEffect(() => {
        const initializeValue = async () => {
            if (!value) {
                setSelectedUserData(null)
                return
            }
            if (typeof value === 'string') {
                const userMini = await getUserById(value)
                if (userMini) {
                    setSelectedUserData(userMini)
                } else {
                    setSelectedUserData({ id: value, username: value, first_name: '', last_name: '', logo: '', premium: false })
                }
            } else if (typeof value === 'object' && 'id' in value) {
                setSelectedUserData(value)
                onChange(value.id)
            }
        }

        initializeValue()
    }, [value])

    const handleSearch = useCallback(async (query: string) => {
        if (!query.trim() || query.length < 2) {
            setSearchResults([])
            setShowResults(false)
            return
        }

        setIsLoading(true)
        try {
            const users = await searchUsersAction(query)
            if (users && Array.isArray(users?.data)) {
                setSearchResults(users?.data)
                setShowResults(true)
            } else {
                setSearchResults([])
            }
        } catch (error) {
            try {
                const fallbackUsers = await searchUsersAction(query)
                if (fallbackUsers && Array.isArray(fallbackUsers?.data)) {
                    setSearchResults(fallbackUsers?.data)
                    setShowResults(true)
                } else {
                    setSearchResults([])
                }
            } catch (fallbackError) {
                setSearchResults([])
            }
        } finally {
            setIsLoading(false)
        }
    }, [])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value
        setSearchQuery(query)

        // Clear previous timer
        if (debounceTimer) {
            clearTimeout(debounceTimer)
        }

        // Set new timer for debounced search
        const timer = setTimeout(() => {
            handleSearch(query)
        }, 300)

        setDebounceTimer(timer)
    }

    const handleUserSelect = (user: IUser) => {
        setSelectedUserData(user)
        // Clear search field after selection - keep it for search purposes only
        setSearchQuery('')
        setShowResults(false)
        onChange(user.id)
    }

    const handleClear = () => {
        setSelectedUserData(null)
        setSearchQuery('')
        setSearchResults([])
        setShowResults(false)
        onChange('')
    }

    return (
        <div className={cn('space-y-2', className)}>
            <Label htmlFor={name}>{label}</Label>

            <div className='relative'>
                <div className='relative'>
                    <IoIosSearch className='absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4' />
                    <Input
                        id={name}
                        type='text'
                        placeholder={placeholder}
                        value={searchQuery}
                        onChange={handleInputChange}
                        disabled={disabled}
                        className='pl-10 pr-10'
                        onFocus={() => searchResults.length > 0 && setShowResults(true)}
                    />
                    {searchQuery && !selectedUserData && (
                        <Button
                            type='button'
                            variant='ghost'
                            size='sm'
                            onClick={() => setSearchQuery('')}
                            disabled={disabled}
                            className='absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0'
                        >
                            <IoMdClose className='h-4 w-4' />
                        </Button>
                    )}
                </div>

                {/* Loading indicator */}
                {isLoading && (
                    <div className='absolute right-12 top-1/2 transform -translate-y-1/2'>
                        <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-border'></div>
                    </div>
                )}

                {/* Search Results Dropdown */}
                {showResults && searchResults.length > 0 && (
                    <div className='absolute top-full left-0 right-0 z-50 mt-1 max-h-64 overflow-y-auto bg-primary/50 backdrop-blur-sm border-border rounded-lg shadow-lg'>
                        <div className='p-1'>
                            {searchResults.map(user => (
                                <button
                                    key={user.id}
                                    type='button'
                                    onClick={() => handleUserSelect(user)}
                                    className='w-full text-left p-3 hover:bg-accent hover:text-accent-foreground rounded-md transition-colors flex items-center gap-3 group'
                                >
                                    <Avatar className='h-8 w-8 flex-shrink-0'>
                                        <AvatarImage src={user.logo} alt={user.username} />
                                        <AvatarFallback>{user.username.charAt(0).toUpperCase()}</AvatarFallback>
                                    </Avatar>
                                    <div className='flex-1 min-w-0'>
                                        <div className='font-medium truncate'>{user.username}</div>
                                        {(user.first_name || user.last_name) && (
                                            <div className='text-sm text-muted-foreground truncate'>
                                                {user.first_name} {user.last_name}
                                            </div>
                                        )}
                                    </div>
                                    <IoMdAdd className='size-5 text-muted-foreground group-hover:text-accent-foreground transition-colors flex-shrink-0' />
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* No results message */}
                {showResults && !isLoading && searchQuery.length >= 2 && searchResults.length === 0 && (
                    <div className='absolute top-full left-0 right-0 z-50 mt-1 bg-primary/50 backdrop-blur-sm border border-border rounded-lg shadow-lg'>
                        <div className='p-4 text-center'>
                            <div className='text-muted-foreground mb-1'>No users found</div>
                            <div className='text-xs text-muted-foreground'>
                                Try searching with a different username or name
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Selected User Display */}
            {selectedUserData &&
                (() => {
                    const avatarInitial =
                        (selectedUserData.username ||
                            selectedUserData.first_name ||
                            selectedUserData.last_name ||
                            '?'
                        )
                            .charAt(0)
                            .toUpperCase()
                    const firstName =
                        selectedUserData.first_name ?? (selectedUserData as { firstName?: string }).firstName ?? ''
                    const lastName =
                        selectedUserData.last_name ?? (selectedUserData as { lastName?: string }).lastName ?? ''
                    return (
                        <div className='flex items-center gap-3 p-3 bg-secondary rounded-md'>
                            <Avatar className='h-10 w-10'>
                                <AvatarImage src={selectedUserData.logo} alt={selectedUserData.username ?? ''} />
                                <AvatarFallback>{avatarInitial}</AvatarFallback>
                            </Avatar>
                            <div className='flex-1'>
                                <div className='font-medium'>
                                    {selectedUserData.username ??
                                        selectedUserData.first_name ??
                                        selectedUserData.last_name ??
                                        '—'}
                                </div>
                                {(selectedUserData.first_name || selectedUserData.last_name) && (
                                    <div className='text-sm text-muted-foreground'>
                                        {firstName} {lastName}
                                    </div>
                                )}
                            </div>
                            <div className='flex items-center gap-2'>
                                <div className='text-xs text-muted-foreground'>Owner selected</div>
                                <Button
                                    type='button'
                                    variant='ghost'
                                    size='sm'
                                    onClick={handleClear}
                                    disabled={disabled}
                                    className='h-8 w-8 p-0 hover:bg-destructive hover:text-destructive-foreground'
                                    title='Remove selected owner'
                                >
                                    <IoMdTrash className='h-4 w-4' />
                                </Button>
                            </div>
                        </div>
                    )
                })()}

            {/* No owner – show when no owner is assigned */}
            {!selectedUserData && (!value || (typeof value === 'string' && value.trim() === '')) && (
                <div className='flex items-center gap-3 p-3 bg-muted rounded-md border border-dashed border-border'>
                    <div className='h-10 w-10 rounded-full bg-muted-foreground/10 flex items-center justify-center'>
                        <IoIosSearch className='h-5 w-5 text-muted-foreground' />
                    </div>
                    <div className='flex-1'>
                        <div className='font-medium text-muted-foreground'>No owner assigned</div>
                        <div className='text-sm text-muted-foreground'>Search and select a user to assign as organisation owner</div>
                    </div>
                </div>
            )}

            {/* Show user ID when no user data is available */}
            {value && !selectedUserData && typeof value === 'string' && searchQuery && (
                <div className='flex items-center gap-3 p-3 bg-secondary rounded-md'>
                    <Avatar className='h-10 w-10'>
                        <AvatarFallback>{searchQuery.charAt(0).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className='flex-1'>
                        <div className='font-medium'>User ID: {searchQuery}</div>
                        <div className='text-sm text-muted-foreground'>Loading user details...</div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default UserSearchSelect
